import type { TFunction } from "i18next";
import { createElement } from "react";
import type {
  TreatmentCardProps,
  TreatmentCardStatusRow,
} from "@noom/wax-component-library";

import CalendarStatusIcon from "../../image_assets/calendar_status_icon.svg";
import ChatBubbleIcon from "../../image_assets/chat_bubble_icon.svg";
import CheckmarkStatusIcon from "../../image_assets/checkmark_status_icon.svg";
import ClipboardIcon from "../../image_assets/clipboard_icon.svg";
import CubeIcon from "../../image_assets/cube_icon.svg";
import DocumentIcon from "../../image_assets/document_icon.svg";
import TruckStatusIcon from "../../image_assets/truck_status_icon.svg";
import {
  formatDateMonthDay,
  formatDateMonthDayYear,
  formatDateLongMonthDayYear,
} from "../../utils/dateUtils";
import {
  MedCtaAction,
  type MedCtaTappedParams,
} from "../nativeAnalytics/medAnalytics";

import type { TreatmentBottomSheetCtaVariant } from "./TreatmentDetailBottomSheet";
import { formatRxSchedule } from "./formatRxSchedule";
import type { TreatmentOverview } from "./hooks/useTreatmentOverviews";
import { renderProgramName } from "./renderProgramName";
import {
  findBiomarkerInstructionsLink,
  isLabTreatmentOverview,
  isOtcTreatmentOverview,
  isRxTreatmentOverview,
  isSupplementOtcTreatment,
  type OtcTreatmentOverview,
  type RxTreatmentOverview,
} from "./treatmentOverviewGuards";
import { openDeeplink, openTrackingUrl } from "./utils";

type TreatmentOverviewStatus = NonNullable<TreatmentOverview["status"]>;
type TreatmentOverviewStatusByTypename<
  Typename extends TreatmentOverviewStatus["__typename"],
> = Extract<TreatmentOverviewStatus, { __typename: Typename }>;

/**
 * Status-row CTA payload assembled by the mapper.
 * The caller injects the `context` field before forwarding.
 */
type MedTreatmentCtaMeta = Omit<MedCtaTappedParams, "context">;

export type TreatmentBottomSheetConfig = {
  body: string;
  ctaLabel: string;
  ctaVariant: TreatmentBottomSheetCtaVariant;
  ctaAction?: MedCtaAction;
  deeplink?: string;
  deeplinkOpenMode?: "deeplink" | "external";
};

export type TreatmentCardMapperOptions = {
  /**
   * When true, supplement card taps open the Treatments detail page instead of
   * the interim product-info bottom sheet. Applies to every supplement, not a
   * per-product allowlist. Biomarker kits are unaffected. Driven by
   * `HIVE_CARE_SUPPLEMENT_TREATMENT_DETAIL`.
   */
  isSupplementTreatmentDetailEnabled?: boolean;
  isBiomarkerTreatmentDetailEnabled?: boolean;
};

/**
 * The interim "kit instructions" purchase-date sheet, shared by the OTC and Lab
 * biomarker branches. The instructions `url` is passed through verbatim — the
 * OTC-2822 catalog seed owns the authenticated-deeplink shape, so the client no
 * longer wraps action-link URLs here.
 */
const buildBiomarkerInstructionsSheet = (
  t: TFunction<"care">,
  label: string,
  url: string
): TreatmentBottomSheetConfig => ({
  body: t("statusBottomSheet.otcPurchaseDate.body"),
  ctaLabel: t("statusBottomSheet.otcPurchaseDate.cta", { defaultValue: label }),
  ctaVariant: "primary",
  deeplink: url,
});

export const getTreatmentBottomSheetConfig = (
  treatment: TreatmentOverview,
  t: TFunction<"care">,
  options?: TreatmentCardMapperOptions
): TreatmentBottomSheetConfig | undefined => {
  const { status } = treatment;

  if (isOtcTreatmentOverview(treatment)) {
    const biomarkerInstructionsLink = findBiomarkerInstructionsLink(treatment);

    // Biomarker test kits are identified by their instructions action link.
    // Show the purchase-date sheet linking to the kit instructions when both
    // the link URL and a purchase date are present; otherwise show no sheet.
    // Never fall through to the supplement copy below, which would be wrong for
    // a lab kit.
    if (biomarkerInstructionsLink) {
      if (!biomarkerInstructionsLink.url || !treatment.purchaseDate) {
        return undefined;
      }
      return buildBiomarkerInstructionsSheet(
        t,
        biomarkerInstructionsLink.label,
        biomarkerInstructionsLink.url
      );
    }

    // When the treatments-detail gate is on, supplements skip this interim
    // sheet and open `/care/treatment/:treatmentCode` instead. Biomarker kits
    // already returned above, so this covers exactly the supplement branch.
    if (
      options?.isSupplementTreatmentDetailEnabled &&
      isSupplementOtcTreatment(treatment)
    ) {
      return undefined;
    }

    // Supplements (the only other OTC type today): a static info sheet noting
    // that shipment tracking is delivered by email. Shown on tap in every
    // shipment status; the "Okay" CTA simply dismisses the sheet.
    return {
      body: t("statusBottomSheet.otcSupplement.body", {
        name: treatment.treatmentName,
      }),
      ctaLabel: t("statusBottomSheet.otcSupplement.cta"),
      ctaVariant: "primary",
    };
  }

  if (isLabTreatmentOverview(treatment)) {
    if (options?.isBiomarkerTreatmentDetailEnabled) {
      return undefined;
    }

    const biomarkerInstructionsLink = findBiomarkerInstructionsLink(treatment);

    if (!biomarkerInstructionsLink?.url) {
      return undefined;
    }

    return buildBiomarkerInstructionsSheet(
      t,
      biomarkerInstructionsLink.label,
      biomarkerInstructionsLink.url
    );
  }

  if (!status) {
    return undefined;
  }

  switch (status.__typename) {
    case "TreatmentOverviewStatusIntakeIncomplete": {
      // Both IntakeIncomplete and InReview bottom sheets are only shown for
      // new intakes. Refill flows have a different UX path and should not
      // surface these sheets. IntakeIncomplete is always Rx, so the cast is safe.
      if ((treatment as RxTreatmentOverview).isRefill) return undefined;
      return {
        body: t("statusBottomSheet.intakeIncomplete.body"),
        ctaLabel: t("statusBottomSheet.intakeIncomplete.cta"),
        ctaVariant: "primary",
        ctaAction: MedCtaAction.FinishIntake,
        deeplink: status.ctaDeeplink,
      };
    }
    case "TreatmentOverviewStatusInReview": {
      // `isRefill` is "True when the clinician is reviewing a refill; false
      // for a new intake" — falls back to the intake copy when null/undefined.
      // It lives on the top-level RxTreatmentOverview; InReview can only be
      // the status of an Rx treatment, so the cast is safe.
      if ((treatment as RxTreatmentOverview).isRefill) return undefined;
      return {
        body: t("statusBottomSheet.intakeInReview.body"),
        ctaLabel: t("statusBottomSheet.intakeInReview.cta"),
        ctaVariant: "secondary",
        ctaAction: MedCtaAction.Chat,
        deeplink: status.ctaDeeplink,
      };
    }
    default:
      return undefined;
  }
};

const formatRxSubtitleSecondary = (
  treatment: RxTreatmentOverview,
  t: TFunction<"care">
): string | undefined => {
  const { latestDoseTaken } = treatment;

  if (!latestDoseTaken) {
    return undefined;
  }

  const { unitsTaken, dosage } = latestDoseTaken;
  const localizedUnit =
    dosage.unit === "EACH"
      ? t("unitsTakenLabel", { count: dosage.amount })
      : t(`doseUnits.${dosage.unit}`, {
          defaultValue: dosage.unit.toLowerCase(),
        });

  // Fall back to the dosage-only format when unitsTaken is absent
  if (!unitsTaken) {
    return t("lastDoseTemplateDosageOnly", {
      amount: dosage.amount,
      unit: localizedUnit,
    });
  }

  return t("lastDoseTemplate", {
    unitsTaken,
    unitsTakenLabel: t("unitsTakenLabel", { count: unitsTaken }),
    amount: dosage.amount,
    unit: localizedUnit,
  });
};

const formatOtcSubtitle = (
  treatment: OtcTreatmentOverview,
  t: TFunction<"care">
): string | undefined => {
  if (!treatment.purchaseDate) {
    return undefined;
  }

  const formattedPurchaseDate = formatDateLongMonthDayYear(
    treatment.purchaseDate
  );

  return formattedPurchaseDate
    ? t("purchaseDate", { date: formattedPurchaseDate })
    : treatment.purchaseDate;
};

export const formatDatedStatusText = (
  t: TFunction<"care">,
  shippedDate: string | null | undefined,
  datedKey: string,
  noDateKey: string
): string => {
  const formattedDate = shippedDate ? formatDateMonthDayYear(shippedDate) : "";
  return formattedDate ? t(datedKey, { date: formattedDate }) : t(noDateKey);
};

const createStatusRowIcon = (src: string) =>
  createElement("img", {
    alt: "",
    "aria-hidden": true,
    height: 16,
    src,
    width: 16,
  });

const calendarStatusRowIcon = createStatusRowIcon(CalendarStatusIcon);
const chatBubbleStatusRowIcon = createStatusRowIcon(ChatBubbleIcon);
const checkmarkStatusRowIcon = createStatusRowIcon(CheckmarkStatusIcon);
const clipboardStatusRowIcon = createStatusRowIcon(ClipboardIcon);
const cubeStatusRowIcon = createStatusRowIcon(CubeIcon);
const documentStatusRowIcon = createStatusRowIcon(DocumentIcon);
const truckStatusRowIcon = createStatusRowIcon(TruckStatusIcon);

const getStatusRow = (
  treatment: TreatmentOverview,
  t: TFunction<"care">,
  handlers?: GetTreatmentCardPropsHandlers
): TreatmentCardStatusRow | undefined => {
  const { status } = treatment;

  if (!status) return undefined;

  const trackAndOpen = (meta: MedTreatmentCtaMeta, deeplink: string) => {
    handlers?.onStatusRowCtaClick?.(meta);
    openDeeplink(deeplink);
  };

  switch (status.__typename) {
    case "TreatmentOverviewStatusIntakeIncomplete": {
      const intakeIncompleteStatus =
        status as TreatmentOverviewStatusByTypename<"TreatmentOverviewStatusIntakeIncomplete">;
      // IntakeIncomplete is always an Rx status, so the cast is safe.
      const isRefill = (treatment as RxTreatmentOverview).isRefill;
      const label = t(
        isRefill ? "status.cta.start" : "status.cta.finishIntake"
      );
      return {
        icon: clipboardStatusRowIcon,
        text: t(
          isRefill ? "status.refillIntakeIncomplete" : "status.intakeIncomplete"
        ),
        label,
        onClick: () =>
          trackAndOpen(
            {
              ctaAction: MedCtaAction.FinishIntake,
              ctaText: label,
              medicationName: treatment.treatmentName,
            },
            intakeIncompleteStatus.ctaDeeplink
          ),
      };
    }
    case "TreatmentOverviewStatusUpdate": {
      const updateStatus =
        status as TreatmentOverviewStatusByTypename<"TreatmentOverviewStatusUpdate">;
      const label = t("status.cta.update");
      return {
        icon: chatBubbleStatusRowIcon,
        text: t("status.update"),
        label,
        onClick: () =>
          trackAndOpen(
            {
              ctaAction: MedCtaAction.Chat,
              ctaText: label,
              medicationName: treatment.treatmentName,
            },
            updateStatus.ctaDeeplink
          ),
      };
    }
    case "TreatmentOverviewStatusRefills": {
      const refillsStatus =
        status as TreatmentOverviewStatusByTypename<"TreatmentOverviewStatusRefills">;
      const formattedRefillDate = formatDateMonthDay(
        refillsStatus.nextRefillDate
      );
      // The copy is date-only, so show no row rather than a dangling
      // "refills on " when the date is missing or unparseable.
      if (!formattedRefillDate) {
        return undefined;
      }
      // Supplements reuse the Refills status for their next EF shipment, so
      // they get shipment copy instead of the prescription refill wording.
      return {
        icon: calendarStatusRowIcon,
        text: t(
          isSupplementOtcTreatment(treatment)
            ? "status.supplementRefills"
            : "status.refills",
          { date: formattedRefillDate }
        ),
      };
    }
    case "TreatmentOverviewStatusInReview": {
      // `isRefill` is "True when the clinician is reviewing a refill; false
      // for a new intake" — falls back to the intake copy when null/undefined.
      // It lives on the top-level RxTreatmentOverview; InReview can only be
      // the status of an Rx treatment, so the cast is safe.
      const { isRefill } = treatment as RxTreatmentOverview;
      return {
        icon: documentStatusRowIcon,
        text: t(isRefill ? "status.refillInReview" : "status.intakeInReview"),
      };
    }
    case "TreatmentOverviewStatusShippingSoon":
      return {
        icon: cubeStatusRowIcon,
        text: t("status.shippingSoon"),
      };
    case "TreatmentOverviewStatusShipped": {
      const shippedStatus =
        status as TreatmentOverviewStatusByTypename<"TreatmentOverviewStatusShipped">;
      // Both `shippedDate` and `trackingUrl` are nullable in the schema
      // ("Null when unavailable") — fall back to the no-date copy when
      // missing, and omit the Track CTA when there's no tracking
      // destination rather than render a no-op button.
      const text = formatDatedStatusText(
        t,
        shippedStatus.shippedDate,
        "status.shipped",
        "status.shippedNoDate"
      );
      const trackingUrl = shippedStatus.trackingUrl;
      if (!trackingUrl) {
        return { icon: truckStatusRowIcon, text };
      }
      const label = t("status.cta.track");
      return {
        icon: truckStatusRowIcon,
        text,
        label,
        onClick: () => {
          handlers?.onStatusRowCtaClick?.({
            ctaAction: MedCtaAction.TrackShipment,
            ctaText: label,
            medicationName: treatment.treatmentName,
          });
          openTrackingUrl(trackingUrl);
        },
      };
    }
    case "TreatmentOverviewStatusReplacementShipped": {
      const replacementShippedStatus =
        status as TreatmentOverviewStatusByTypename<"TreatmentOverviewStatusReplacementShipped">;
      // Mirrors the Shipped case: both `shippedDate` and `trackingUrl` are nullable
      // ("Null when unavailable") — fall back to the no-date copy when missing, and
      // omit the Track CTA when there's no tracking destination.
      const text = formatDatedStatusText(
        t,
        replacementShippedStatus.shippedDate,
        "status.replacementShipped",
        "status.replacementShippedNoDate"
      );
      const trackingUrl = replacementShippedStatus.trackingUrl;
      if (!trackingUrl) {
        return { icon: truckStatusRowIcon, text };
      }
      const label = t("status.cta.track");
      return {
        icon: truckStatusRowIcon,
        text,
        label,
        // Mirror the Shipped case: a carrier tracking URL must open via
        // openTrackingUrl (synthetic anchor click) rather than trackAndOpen's
        // openDeeplink, which fails for external links in mobile webviews.
        onClick: () => {
          handlers?.onStatusRowCtaClick?.({
            ctaAction: MedCtaAction.TrackShipment,
            ctaText: label,
            medicationName: treatment.treatmentName,
          });
          openTrackingUrl(trackingUrl);
        },
      };
    }
    case "TreatmentOverviewStatusDelivered":
      return {
        icon: checkmarkStatusRowIcon,
        text: t("status.delivered"),
      };
    default:
      return undefined;
  }
};

export type GetTreatmentCardPropsHandlers = {
  onShowBottomSheet?: (treatment: TreatmentOverview) => void;
  onOpenTreatmentDetail?: (treatment: TreatmentOverview) => void;
  onCardTapped?: (treatment: TreatmentOverview) => void;
  onStatusRowCtaClick?: (meta: MedTreatmentCtaMeta) => void;
};

export const getTreatmentCardProps = (
  treatment: TreatmentOverview,
  t: TFunction<"care">,
  handlers?: GetTreatmentCardPropsHandlers,
  options?: TreatmentCardMapperOptions
): TreatmentCardProps => {
  const hasSheet = !!getTreatmentBottomSheetConfig(treatment, t, options);
  const isOTC = isOtcTreatmentOverview(treatment);
  const openSupplementDetail =
    !!options?.isSupplementTreatmentDetailEnabled &&
    isSupplementOtcTreatment(treatment);
  const onShowBottomSheet = handlers?.onShowBottomSheet;
  const onOpenTreatmentDetail = handlers?.onOpenTreatmentDetail;
  const onCardTapped = handlers?.onCardTapped;

  // Rx cards open the treatments detail page. Supplements do the same when
  // the detail gate is on; otherwise OTC keeps the interim bottom sheet
  // (biomarkers always keep theirs). Remaining OTC cards fall through to
  // the legacy native deeplink when present.
  const destination =
    openSupplementDetail && onOpenTreatmentDetail
      ? () => onOpenTreatmentDetail(treatment)
      : hasSheet && onShowBottomSheet
        ? () => onShowBottomSheet(treatment)
        : onOpenTreatmentDetail && !isOTC
          ? () => onOpenTreatmentDetail(treatment)
          : treatment.deeplink
            ? () => openDeeplink(treatment.deeplink as string)
            : undefined;

  const cardOnClick = destination
    ? () => {
        onCardTapped?.(treatment);
        destination();
      }
    : undefined;

  const cardProps: TreatmentCardProps = {
    programName: treatment.formattedProgramName
      ? renderProgramName(treatment.formattedProgramName)
      : undefined,
    title: treatment.treatmentName,
    imageBgUrl: treatment.productImageUrl,
    imageBgColor: treatment.productBackgroundColor ?? undefined,
    imageHeight: "85px",
    statusRow: getStatusRow(treatment, t, handlers),
    onClick: cardOnClick,
  };

  if (isRxTreatmentOverview(treatment)) {
    return {
      ...cardProps,
      subtitle: formatRxSchedule(treatment, t),
      subtitleSecondary: formatRxSubtitleSecondary(treatment, t),
      clinician: treatment.clinicianDetail
        ? {
            name: treatment.clinicianDetail.name,
            avatarUrl: treatment.clinicianDetail.photoUrl ?? undefined,
          }
        : undefined,
    };
  }

  if (isOtcTreatmentOverview(treatment)) {
    return {
      ...cardProps,
      subtitle: formatOtcSubtitle(treatment, t),
    };
  }

  return cardProps;
};
